import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppStore } from '../store/AppStore';
import { SoundSettingsPanel } from '../sound/SoundSettingsControl';
import { useReaction } from './ReactionProvider';

function ActionIcon({ type }: { type: 'recommend' | 'learn' | 'hint' | 'sound' }) {
  const common = { width: 22, height: 22, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.9, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, 'aria-hidden': true };
  const paths: Record<typeof type, ReactNode> = {
    recommend: <><path d="m12 3 2.2 4.7 5.1.7-3.7 3.6.9 5.1-4.5-2.4-4.5 2.4.9-5.1-3.7-3.6 5.1-.7Z"/><path d="M18.5 16.5 21 19l-2.5 2.5"/></>,
    learn: <><path d="M4 5.5A3.5 3.5 0 0 1 7.5 2H12v18H7.5A3.5 3.5 0 0 0 4 23Z"/><path d="M20 5.5A3.5 3.5 0 0 0 16.5 2H12v18h4.5A3.5 3.5 0 0 1 20 23Z"/></>,
    hint: <><path d="M9 18h6M10 21h4"/><path d="M8.2 14.5A6 6 0 1 1 15.8 14.5c-.9.7-1.3 1.4-1.3 2H9.5c0-.6-.4-1.3-1.3-2Z"/></>,
    sound: <><path d="M4 10v4h4l5 4V6l-5 4H4Z"/><path d="M16 9.5a4 4 0 0 1 0 5M18.5 7a7.5 7.5 0 0 1 0 10"/></>,
  };
  return <svg {...common}>{paths[type]}</svg>;
}

function hintFor(pathname: string): string {
  if (pathname.includes('mission')) return '정답을 빨리 찾기보다, 왜 그렇게 골랐는지 한 문장으로 말해 보세요.';
  if (pathname.includes('world') || pathname.includes('concept')) return '공간 하나만 골라도 충분해요. 모르는 말은 설옹의 설명부터 열어 보세요.';
  if (pathname.includes('weekly') || pathname.includes('badge') || pathname.includes('passport')) return '완료 개수보다 달라진 습관 한 가지를 찾아보면 성장이 더 잘 보여요.';
  if (pathname.includes('family') || pathname.includes('fridge')) return '결과보다 선택한 이유를 가족에게 먼저 이야기해 보세요.';
  return '오늘은 2분짜리 작은 행동 하나면 충분해요. 기록이나 미션 중 더 쉬운 것부터 시작해요.';
}

export function SnowsAssistantControl() {
  const { state } = useAppStore();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { emit } = useReaction();
  const [open, setOpen] = useState(false);
  const [hintOpen, setHintOpen] = useState(false);
  const [soundOpen, setSoundOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLElement>(null);

  const assignedMission = useMemo(() => state?.missions.find((mission) => mission.status === 'assigned'), [state?.missions]);
  const recommendation = assignedMission
    ? { title: assignedMission.title, note: '진행 중인 오늘의 미션을 이어가요.', to: '/child/missions' }
    : { title: '오늘의 돈 기록 한 줄', note: '가장 부담이 적은 행동부터 시작해요.', to: '/child/records' };
  const hasRecommendation = Boolean(assignedMission);

  const close = (restoreFocus = true) => {
    setOpen(false);
    setHintOpen(false);
    setSoundOpen(false);
    if (restoreFocus) window.requestAnimationFrame(() => triggerRef.current?.focus());
  };

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const focusable = () => [...(panelRef.current?.querySelectorAll<HTMLElement>('button, a[href], input, summary, [tabindex]:not([tabindex="-1"])') ?? [])]
      .filter((element) => !element.hasAttribute('disabled') && element.getAttribute('aria-hidden') !== 'true');
    window.requestAnimationFrame(() => focusable()[0]?.focus());
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = focusable();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const openAssistant = () => {
    setOpen(true);
    void emit('assistant.opened');
  };

  const goTo = (to: string, message: string) => {
    void emit('assistant.guidance', { message });
    close(false);
    navigate(to);
  };

  const toggleHint = () => {
    const next = !hintOpen;
    setHintOpen(next);
    setSoundOpen(false);
    if (next) void emit('assistant.guidance', { message: hintFor(pathname) });
  };

  return <div className={`snows-assistant-nav ${hasRecommendation ? 'has-recommendation' : ''}`}>
    <button
      ref={triggerRef}
      className="snows-assistant-trigger"
      type="button"
      aria-label="스노스 도우미 열기"
      aria-expanded={open}
      aria-controls="snows-assistant-panel"
      onClick={openAssistant}
      data-sound="none"
    >
      <span className="snows-assistant-orb" aria-hidden="true"><img src="/assets/characters/snows/snows_wave_v1_512.png" alt="" /></span>
      {hasRecommendation && <span className="snows-assistant-notice" aria-hidden="true" />}
      <span className="navigation-label">도우미</span>
    </button>

    {open && <>
      <button className="snows-assistant-backdrop" type="button" aria-label="스노스 도우미 닫기" onClick={() => close()} />
      <section ref={panelRef} id="snows-assistant-panel" className="snows-assistant-panel" role="dialog" aria-modal="true" aria-labelledby="snows-assistant-title">
        <header>
          <span className="snows-assistant-avatar" aria-hidden="true"><img src="/assets/characters/snows/snows_wave_v1_512.png" alt="" /></span>
          <div><small>스노스 도우미</small><strong id="snows-assistant-title">무엇을 도와줄까?</strong></div>
          <button className="snows-assistant-close" type="button" aria-label="도우미 닫기" onClick={() => close()}>×</button>
        </header>

        <button className="snows-assistant-recommendation" type="button" onClick={() => goTo(recommendation.to, `${recommendation.title}부터 시작해 볼까요?`)}>
          <span><ActionIcon type="recommend" /></span>
          <div><small>오늘 추천</small><strong>{recommendation.title}</strong><p>{recommendation.note}</p></div>
          <b aria-hidden="true">›</b>
        </button>

        <div className="snows-assistant-actions">
          <button type="button" onClick={() => goTo('/child/concepts', '궁금한 경제 개념을 쉬운 설명으로 살펴봐요.')}><span><ActionIcon type="learn" /></span><strong>개념 쉽게 보기</strong><small>도서관으로 이동</small></button>
          <button type="button" aria-expanded={hintOpen} aria-controls="snows-assistant-hint" onClick={toggleHint}><span><ActionIcon type="hint" /></span><strong>힌트와 팁</strong><small>현재 화면 도움</small></button>
          <button type="button" aria-expanded={soundOpen} aria-controls="snows-assistant-sound" onClick={() => { setSoundOpen((value) => !value); setHintOpen(false); }}><span><ActionIcon type="sound" /></span><strong>소리·액션</strong><small>효과음과 모션</small></button>
        </div>

        {hintOpen && <aside id="snows-assistant-hint" className="snows-assistant-hint" role="status"><strong>스노스 팁</strong><p>{hintFor(pathname)}</p></aside>}
        {soundOpen && <div id="snows-assistant-sound" className="snows-assistant-sound"><SoundSettingsPanel /></div>}
      </section>
    </>}
  </div>;
}
